import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  alpha,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-hot-toast';
import { useMutation } from 'convex/react';
import { useSessionMutation } from '../../hooks/useSession';
import { api } from '../../convex/_generated/api';
import type { PlantAnalysisResponse } from '../../types/plantAnalysis';
import PlantIdentificationResult from '../PlantIdentificationResult';
import { usePlantAnalysis } from '../../hooks/useAI';
import { usePlantLists } from '../../hooks/usePlantLists';
import { useAuth } from '../../contexts/AuthContext';

// Rejtett kamera input stílus
const HiddenCameraInput = styled('input')({
  display: 'none',
});

const UploadArea = styled(Box)<{ hasImage: boolean }>(({ theme, hasImage }) => ({
  border: `2px dashed ${hasImage ? theme.palette.success.main : theme.palette.grey[400]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: hasImage
    ? alpha(theme.palette.success.main, 0.1)
    : alpha(theme.palette.grey[100], 0.5),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: theme.palette.primary.main,
  },
}));

// Styled icon button for the header
export const PlantIdentifierButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: alpha(theme.palette.common.white, 0.1),
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
    borderColor: alpha(theme.palette.common.white, 0.4),
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  // Long press visual feedback
  '&.pressing': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.3),
    borderColor: theme.palette.secondary.main,
  },
}));

interface QuickPlantIdentifierProps {
  onFinished?: () => void;
  /** If true, the dialog will open automatically (controlled from outside) */
  externalOpen?: boolean;
  /** Callback when external open state changes */
  onExternalOpenChange?: (open: boolean) => void;
  /** If true, renders only the dialog without the button (for external trigger) */
  dialogOnly?: boolean;
}

const QuickPlantIdentifier: React.FC<QuickPlantIdentifierProps> = ({
  onFinished,
  externalOpen,
  onExternalOpenChange,
  dialogOnly = false,
}) => {
  // Dialog state
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);

  // Use external control if provided, otherwise internal
  const dialogOpen = externalOpen !== undefined ? externalOpen : internalDialogOpen;
  const setDialogOpen = (open: boolean) => {
    if (onExternalOpenChange) {
      onExternalOpenChange(open);
    }
    setInternalDialogOpen(open);
  };
  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  // Image state
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Analysis state
  const [analysisResult, setAnalysisResult] = useState<PlantAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Convex hooks
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const { identifyPlant } = usePlantAnalysis();
  const createPendingFromIdentification = useSessionMutation(
    api.plantIdentification.createPendingFromIdentification
  );
  const { plantLists, isLoading: loadingLists } = usePlantLists(user?._id ?? null);

  // Plant list state
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  // Long press handling
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPressing, setIsPressing] = useState(false);
  const isLongPress = useRef(false);

  // Input refs
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Long press duration (ms)
  const LONG_PRESS_DURATION = 500;

  // Auto-select first plant list
  useEffect(() => {
    if (plantLists && plantLists.length > 0 && !selectedListId) {
      setSelectedListId(plantLists[0]._id);
    }
  }, [plantLists, selectedListId]);

  // Handle long press start
  const handlePressStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    isLongPress.current = false;
    setIsPressing(true);

    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setIsPressing(false);
      // Long press: directly open camera
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    }, LONG_PRESS_DURATION);
  }, []);

  // Handle press end
  const handlePressEnd = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setIsPressing(false);

    // If it was not a long press, open the dialog
    if (!isLongPress.current) {
      setDialogOpen(true);
    }
  }, []);

  // Handle press cancel (e.g., finger moved away)
  const handlePressCancel = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setIsPressing(false);
    isLongPress.current = false;
  }, []);

  // Handle camera capture
  const handleCameraCapture = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setAnalysisResult(null);

    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result as string);
      // Open dialog to select list and analyze
      setDialogOpen(true);
    };
    reader.readAsDataURL(file);

    // Reset input so the same file can be selected again
    e.target.value = '';
  }, []);

  // Handle file upload (from dialog)
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setAnalysisResult(null);

    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  }, []);

  // Handle analyze - Convex action hívás
  const handleAnalyze = async () => {
    if (!uploadedImage) {
      toast.error('Kérem töltsön fel egy képet!');
      return;
    }

    if (!selectedListId) {
      toast.error('Kérem válasszon ki egy növény listát!');
      return;
    }

    setLoading(true);
    try {
      const result = await identifyPlant({ imageUrl: uploadedImage });

      if (result.success && result.identification) {
        const id = result.identification;
        // Confidence normalizálás (magyar → angol)
        const confidenceMap: Record<string, string> = { magas: 'high', közepes: 'medium', alacsony: 'low' };
        const normalizedConfidence = confidenceMap[id.confidence?.toLowerCase()] || id.confidence || 'medium';

        // Pending növény létrehozása (vagy dedup), hogy megjelenjen a
        // jóváhagyandók között.
        let pendingStatus: string | undefined;
        let pendingMessage = '';
        try {
          const saved = await createPendingFromIdentification({
            identification: id,
          });
          if (saved?.success) {
            pendingStatus = saved.status;
            pendingMessage = saved.message || '';
          }
        } catch (e) {
          console.error('Pending növény létrehozási hiba:', e);
        }

        const response: PlantAnalysisResponse = {
          success: true,
          identified: true,
          plantExists: pendingStatus === 'exists',
          analysis: {
            identified: true,
            confidence: normalizedConfidence,
            latin_name: id.latinName || '',
            hungarian_name: id.hungarianName || '',
            plant_type: id.category || 'Ismeretlen',
            characteristics: id,
            notes: id.description,
          },
          message:
            pendingMessage ||
            `Növény azonosítva: ${id.hungarianName || ''} (${id.latinName || ''})`,
        };

        setAnalysisResult(response);
        setDialogOpen(false);
        setResultDialogOpen(true);
        toast.success('Kép sikeresen elemezve!');
      } else {
        toast.error(result.error || 'Hiba történt az elemzés során');
      }
    } catch (error) {
      toast.error('Hiba történt a kép elemzése során');
      console.error('Analysis error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset state
  const reset = () => {
    setUploadedImage(null);
    setUploadedFile(null);
    setAnalysisResult(null);
    setDialogOpen(false);
    setResultDialogOpen(false);
    setLoading(false);
  };

  const handleDialogClose = () => {
    if (!loading) {
      setDialogOpen(false);
    }
  };

  const handleResultDialogClose = () => {
    setResultDialogOpen(false);
    reset();
  };

  return (
    <>
      {/* Hidden camera input for direct capture (long press) */}
      <HiddenCameraInput
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
      />

      {/* Hidden file input for gallery selection (dialog) */}
      <HiddenCameraInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileUpload}
      />

      {/* Quick identifier button - only render if not dialogOnly */}
      {!dialogOnly && (
        <Tooltip
          title="Növény felismerés (hosszú nyomás = gyors kamera)"
          enterDelay={500}
        >
          <PlantIdentifierButton
            className={isPressing ? 'pressing' : ''}
            onTouchStart={handlePressStart}
            onTouchEnd={handlePressEnd}
            onTouchCancel={handlePressCancel}
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressCancel}
            aria-label="Növény felismerés"
          >
            <CameraAltIcon />
          </PlantIdentifierButton>
        </Tooltip>
      )}

      {/* Main dialog for image selection and analysis */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: { borderRadius: fullScreen ? 0 : 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalFloristIcon color="primary" />
          Gyors Növény Felismerés
          <IconButton
            onClick={handleDialogClose}
            disabled={loading}
            sx={{ ml: 'auto' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Plant list selector */}
          <Box sx={{ mb: 3 }}>
            {loadingLists ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : plantLists && plantLists.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel id="quick-plant-list-select-label">Növény lista</InputLabel>
                <Select
                  labelId="quick-plant-list-select-label"
                  value={selectedListId || ''}
                  label="Növény lista"
                  onChange={(e) => setSelectedListId(e.target.value as string)}
                  disabled={loading}
                >
                  {plantLists.map((list: any) => (
                    <MenuItem key={list._id} value={list._id}>
                      {list.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="warning">
                Nincs elérhető növény lista. Kérem hozzon létre egyet először.
              </Alert>
            )}
          </Box>

          {/* Image upload/preview area */}
          {uploadedImage ? (
            <Box>
              <UploadArea
                hasImage
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                <img
                  src={uploadedImage}
                  alt="uploaded"
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Kattintson új kép választásához
                </Typography>
              </UploadArea>
            </Box>
          ) : (
            <UploadArea
              hasImage={false}
              onClick={() => fileInputRef.current?.click()}
            >
              <CameraAltIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Kattintson kép kiválasztásához
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                vagy használja az alábbi gombokat
              </Typography>
            </UploadArea>
          )}

          {/* Camera and gallery buttons in dialog */}
          <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CameraAltIcon />}
              onClick={() => cameraInputRef.current?.click()}
              disabled={loading}
            >
              Fényképezés
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhotoLibraryIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              Galéria
            </Button>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={reset}
            disabled={loading}
            color="inherit"
          >
            Mégse
          </Button>
          <Button
            variant="contained"
            onClick={handleAnalyze}
            disabled={loading || !uploadedFile || !selectedListId}
            startIcon={loading ? <CircularProgress size={20} /> : <LocalFloristIcon />}
          >
            {loading ? 'Elemzés...' : 'Növény Felismerése'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result dialog */}
      {analysisResult && (
        <PlantIdentificationResult
          open={resultDialogOpen}
          onClose={handleResultDialogClose}
          onSuccess={() => {
            handleResultDialogClose();
            onFinished?.();
          }}
          result={analysisResult}
          imageUrl={uploadedImage || undefined}
        />
      )}
    </>
  );
};

export default QuickPlantIdentifier;
